<template>
  <div class="success-container" data-testid="request-content-success">
    <div class="success-icon">
      <!-- Success icon SVG -->
      <svg width="120" height="120" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="60" cy="60" r="60" fill="#F0FDF4" />
        <circle cx="60.0002" cy="60" r="46.6667" fill="#DCFCE7" />
        <circle cx="60" cy="60" r="32" fill="#4ADE80" />
        <g filter="url(#filter0_d_14053_2813)">
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M53.197 43.5754C55.3539 42.682 57.6656 42.2222 60.0003 42.2222C64.7152 42.2222 69.2371 44.0952 72.571 47.4292C75.905 50.7631 77.778 55.285 77.778 59.9999C77.778 64.7149 75.905 69.2368 72.571 72.5707C69.2371 75.9047 64.7152 77.7777 60.0003 77.7777H44.0003C43.6483 77.7925 43.2998 77.7024 42.9991 77.5189C42.6984 77.3353 42.459 77.0665 42.3114 76.7466C42.1752 76.4229 42.138 76.066 42.2045 75.7212C42.2709 75.3763 42.438 75.0588 42.6847 74.8088L46.2403 71.2533C43.6283 68.0862 42.2069 64.1051 42.2225 59.9999C42.2225 55.285 44.0955 50.7631 47.4295 47.4292C49.0803 45.7783 51.0401 44.4688 53.197 43.5754ZM48.2847 74.2222H60.0003C63.2905 74.2196 66.4781 73.0763 69.0199 70.987C71.5617 68.8978 73.3005 65.9919 73.94 62.7644C74.5796 59.5369 74.0803 56.1875 72.5273 53.2869C70.9742 50.3862 68.4635 48.1138 65.4229 46.8567C62.3822 45.5996 58.9998 45.4357 55.8518 46.3928C52.7039 47.3499 49.9852 49.3689 48.1589 52.1058C46.3326 54.8426 45.5117 58.128 45.8361 61.4022C46.1606 64.6765 47.6102 67.7369 49.938 70.0622C50.2691 70.3953 50.455 70.8458 50.455 71.3155C50.455 71.7852 50.2691 72.2357 49.938 72.5688L48.2847 74.2222ZM58.8149 66.0784L68.2964 56.6666C69.0865 55.8823 69.0865 54.7058 68.2964 53.9215C67.5062 53.1372 66.321 53.1372 65.5309 53.9215L57.4322 61.9607L54.4692 59.0196C53.6791 58.2352 52.4939 58.2352 51.7038 59.0196C50.9136 59.8039 50.9136 60.9803 51.7038 61.7647L56.0494 66.0784C56.8396 66.8627 58.0247 66.8627 58.8149 66.0784Z"
            fill="url(#paint0_linear_14053_2813)"
          />
        </g>
        <defs>
          <filter
            id="filter0_d_14053_2813"
            x="38.1724"
            y="40.2222"
            width="43.6055"
            height="43.5571"
            filterUnits="userSpaceOnUse"
            color-interpolation-filters="sRGB"
          >
            <feFlood flood-opacity="0" result="BackgroundImageFix" />
            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
            <feOffset dy="2" />
            <feGaussianBlur stdDeviation="2" />
            <feComposite in2="hardAlpha" operator="out" />
            <feColorMatrix type="matrix" values="0 0 0 0 0.0504047 0 0 0 0 0.761249 0 0 0 0 0.309767 0 0 0 1 0" />
            <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_14053_2813" />
            <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_14053_2813" result="shape" />
          </filter>
          <linearGradient id="paint0_linear_14053_2813" x1="59.9752" y1="42.2222" x2="59.9752" y2="77.7793" gradientUnits="userSpaceOnUse">
            <stop stop-color="#D6F0DF" />
            <stop offset="1" stop-color="white" />
          </linearGradient>
        </defs>
      </svg>

      <div class="success-content">
        <h4 class="success-title" data-testid="success-title">Your request link is ready</h4>
        <a :href="requestLink" target="_blank" :title="requestLink" class="success-link" data-testid="success-link">{{ requestLink }}</a>
      </div>
    </div>

    <div class="success-actions">
      <div class="action-item">
        <Button type="link" variant="blue" text="Copy link" icon="general.copy" @click="copyUrl" data-testid="copy-link-button" />
      </div>

      <div class="action-item">
        <Button type="link" variant="blue" text="Open" icon="general.externalLink" @click="openUrl" data-testid="open-link-button" />
      </div>

      <div class="action-item">
        <Button type="link" variant="blue" text="Edit URL" icon="general.pencil" @click="editUrl" data-testid="edit-url-button" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { Button } from '@/storybook';
import type { RequestContent } from '../../types';
import { useToast } from '@/composables/useToast';

interface Props {
  item: RequestContent;
  domain: string;
}

const props = defineProps<Props>();

interface Emits {
  close: [];
  editUrl: [];
}

const emit = defineEmits<Emits>();

const { success: toastSuccess } = useToast();

const requestLink = computed(() => {
  // Always compute the request link using the latest customUrl or slug
  // This ensures the URL reflects the most recent update
  const urlPath = props.item.customUrl || props.item.slug;
  return `${props.domain}/re/${urlPath}`;
});

const copyUrl = async () => {
  try {
    await navigator.clipboard.writeText(requestLink.value);
    toastSuccess('Request video link copied to clipboard');
    emit('close');
  } catch (error) {
    console.error('Failed to copy to clipboard:', error);
  }
};

const openUrl = () => {
  window.open(requestLink.value, '_blank');
};

const editUrl = () => {
  emit('editUrl');
};
</script>

<style scoped>
.success-container {
  @apply flex flex-col items-center gap-4 py-4;
}

.success-icon {
  @apply flex flex-col items-center gap-5;
}

.success-content {
  @apply flex flex-col items-center text-center;
  border-bottom: 1px solid theme('colors.zinc.200');
  @apply dark:border-zinc-700 pb-5;
}

.success-title {
  @apply text-lg font-semibold text-zinc-900 dark:text-zinc-100 mb-2;
}

.success-link {
  @apply text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300
         underline break-all max-w-md;
}

.success-actions {
  @apply flex flex-row gap-4;
}

.action-item {
  @apply flex items-center;
}
</style>
